// Types for the Nine Setup pipeline — the `nine_setup` table, which tracks a
// prospective business from a captain's first outreach ("targeted") all the way
// through curation to a live NineFinds page ("onboarded").
//
// Distinct from OnboardingObject.ts: that models `pending_businesses` (a
// captain's paid submission awaiting admin approval). `nine_setup` is the wider
// sales + curation funnel and has its own table, statuses and REST endpoints.
//
// API JSON is camelCase (new-convention endpoints), so there are no Raw*
// mirrors — the backendToUi converter only null-guards, coerces numbers and
// normalises the status string. The one exception is the onboarding-calendar
// report (bottom of this file), whose nested wire shape differs enough from
// the UI model to warrant explicit *DTO interfaces.
//
// ─────────────────────────────────────────────────────────────────────────────
// RECONCILED against the User Service OpenAPI spec on 2026-08-01
// (https://ninefinds.com/api/v1/user/v3/api-docs → nine-setup-controller):
//  1. Fee units — SETTLED. `onboardingFee` / `curatorFee` are `integer/int32`
//     whole rupees on NineSetupDTO, so they pass through as sent and render as
//     ₹, matching OnboardingObject.onboardingFee. NINE_SETUP_FEES_ARE_IN_PAISE
//     in backendToUi/nineSetupConvertor stays false.
//  2. Primary key — CONFIRMED `id` on the wire, both on NineSetupDTO and in the
//     update request body. Exposed as `setupId` here (repo convention names PKs
//     explicitly — pendingId, captainId, businessId); the converter still
//     accepts either spelling.
//  3. Status casing. The spec types `status` as a bare string, so the PG enum's
//     lower_snake form is what these unions use. The converter lowercases
//     whatever arrives, so an UPPER_SNAKE backend also works without a change.
//  4. Captain identity — CONFIRMED. NineSetupCreateRq has no `captainId` field;
//     it is derived from the bearer token, exactly like create-pending-business.
//     NineSetupDTO returns it. A lead filing rows on another captain's behalf
//     would need a backend change, not a client one.
//  5. Wire name mismatch. The DTO calls the media flag `mediaRequired`; this
//     file calls it `isMediaRequired`. The rename is applied in both converters
//     and nowhere else.
//  6. Create is narrower than update. NineSetupCreateRq accepts neither
//     `status`, `statusNote`, `curatorId` nor `businessId` — a row is born at
//     the default status and acquires those through update-nine-setup.
// ─────────────────────────────────────────────────────────────────────────────

import type { PaymentStatus } from "./OnboardingObject";

/**
 * RETIRED STATUSES — read this before adding one back.
 *
 * `contacted`, `shelved`, `paid` and `media_collected` were removed from these
 * unions on 2026-08-02. They are unreachable in the workflow UI: no transition
 * leads to them and no screen lets a row be MOVED to them.
 *
 * SANCTIONED EXCEPTION (read-only reporting): the admin Onboarding Calendar
 * reports on historical rows, so `contacted`, `paid` and `media_collected` live
 * on in RETIRED_STATUSES / CalendarStatus below. That is the ONLY place they may
 * appear — never add them back to NineSetupStatus, NINE_SETUP_STATUSES or the
 * transition table. (`shelved` is not reported on, so it is not listed there.)
 *
 * ⚠️ THE CLIENT CANNOT MAKE THIS SAFE ALONE. `toNineSetupStatus` in
 * backendToUi/nineSetupConvertor validates against NINE_SETUP_STATUSES and falls
 * back to 'targeted'. So if a row in the database still holds one of the four,
 * it renders as Targeted on every fetch — and the transition popup will offer
 * the sales flow again on a business that already paid.
 *
 * The PG enum and any existing rows MUST be migrated to match. Until that's
 * confirmed, the converter logs a warning when it sees an unknown value rather
 * than swallowing it silently.
 *
 * The lifecycle timestamps (`contactedAt`, `paidAt`, `mediaCollectedAt`) are
 * deliberately KEPT on the row type — they're columns the server may still
 * stamp, and they're independent of whether a status is selectable.
 *
 * Captain / sales phase. The row is owned by `captainId` throughout.
 * - targeted:        identified as a prospect, no contact made yet (DB default)
 * - rejected:        business said no              (terminal)
 * - accepted:        business agreed to onboard    → stamps acceptedAt
 * - draft_submitted: the captain has created the draft business and it is
 *                    waiting on admin approval, which moves it to `approved`
 */
export type NineSetupCaptainStatus =
  | "targeted"
  | "rejected"
  | "accepted"
  | "draft_submitted";

/**
 * Curation phase. From `claimed` onward the row also has a `curatorId`.
 * - approved:        cleared for curation
 * - claimed:         a curator picked it up      → stamps claimedAt
 * - in_progress:     page being built
 * - blocked:         stalled; reason belongs in statusNote
 * - in_review:       submitted for QA            → stamps submittedAt
 * - completed:       QA passed
 * - onboarded:       live business page exists   → stamps onboardedAt, sets businessId
 */
export type NineSetupCurationStatus =
  | "approved"
  | "media_collected"
  | "claimed"
  | "in_progress"
  | "blocked"
  | "in_review"
  | "completed"
  | "welcome_kit_prepared"
  | "handed_over"
  | "onboarded";

export type NineSetupStatus = NineSetupCaptainStatus | NineSetupCurationStatus;

/**
 * Runtime allowlists, ordered as the PG enum declares them so they can drive
 * filter chips and progress indicators directly.
 *
 * These are the single source of truth for "is this a valid status?" — the
 * backendToUi converter validates against NINE_SETUP_STATUSES rather than
 * repeating the list. Adding a value to the PG enum means adding it to the
 * union above AND to the matching array here.
 */
export const NINE_SETUP_CAPTAIN_STATUSES: readonly NineSetupCaptainStatus[] = [
  "targeted",
  "rejected",
  "accepted",
  "draft_submitted",
];

export const NINE_SETUP_CURATION_STATUSES: readonly NineSetupCurationStatus[] = [
  "approved",
  "media_collected",
  "claimed",
  "in_progress",
  "blocked",
  "in_review",
  "completed",
  "welcome_kit_prepared",
  "handed_over",
  "onboarded",
];

export const NINE_SETUP_STATUSES: readonly NineSetupStatus[] = [
  ...NINE_SETUP_CAPTAIN_STATUSES,
  ...NINE_SETUP_CURATION_STATUSES,
];

/** Historical statuses the Onboarding Calendar may still report on. See the
 *  RETIRED STATUSES note at the top of this file — read-only, never selectable
 *  as a transition target. */
export type RetiredStatus = "contacted" | "paid";

export const RETIRED_STATUSES: readonly RetiredStatus[] = [
  "contacted",
  "paid",
];

/** Every status the calendar report can be filtered by: the live funnel plus
 *  the retired historical ones. */
export type CalendarStatus = NineSetupStatus | RetiredStatus;

/** Derived, not re-listed — NINE_SETUP_STATUSES and RETIRED_STATUSES stay the
 *  single sources of truth. */
export const CALENDAR_STATUSES: readonly CalendarStatus[] = [
  ...NINE_SETUP_STATUSES,
  ...RETIRED_STATUSES,
];

/** DB default for a freshly created row (`status nine_setup_status DEFAULT 'targeted'`). */
export const DEFAULT_NINE_SETUP_STATUS: NineSetupStatus = "targeted";

/** True for the sales half of the funnel — use to route a row to the captain
 *  view vs. the curator view instead of hard-coding status lists at call sites. */
export function isCaptainPhaseStatus(
  status: NineSetupStatus
): status is NineSetupCaptainStatus {
  return (NINE_SETUP_CAPTAIN_STATUSES as readonly string[]).includes(status);
}

/** True for the curation half of the funnel (`approved` onward) — the
 *  counterpart of isCaptainPhaseStatus. */
export function isCurationPhaseStatus(
  status: NineSetupStatus
): status is NineSetupCurationStatus {
  return (NINE_SETUP_CURATION_STATUSES as readonly string[]).includes(status);
}

/**
 * STOP-GAP mirror of the backend's commission-credit rule, used only when a row
 * arrives without `commissionCreditedAt` / `curatorCommissionCreditedAt` (see
 * those fields). The server stamps the credit; this just answers "would it have
 * been credited by now?" so a missing field can't turn every row into Pending.
 * Delete once both stamps are confirmed on the wire.
 *  - captain + lead commissions are credited at `onboarded`
 *  - the curator commission is credited from `completed` onward
 */
const CAPTAIN_COMMISSION_CREDITED_STATUSES: readonly NineSetupStatus[] = ["onboarded"];
const CURATOR_FIXED_CREDITED_STATUSES: readonly NineSetupStatus[] = [
  "completed",
  "welcome_kit_prepared",
  "handed_over",
  "onboarded",
];
const CURATOR_VARIABLE_CREDITED_STATUSES: readonly NineSetupStatus[] = ["onboarded"];

/** Whether a captain/lead commission is credited: the server's stamp when it
 *  sent one, else the status rule above. */
export const isCaptainCommissionCredited = (setup: NineSetup): boolean =>
  setup.commissionCreditedAt === undefined
    ? CAPTAIN_COMMISSION_CREDITED_STATUSES.includes(setup.status)
    : setup.commissionCreditedAt !== null;

/** Whether the lead's cut is credited: the server's lead stamp when sent (it can come
 *  earlier than the captain's, at approval), else the captain/lead rule. */
export const isLeadCommissionCredited = (setup: NineSetup): boolean =>
  setup.leadCommissionCreditedAt === undefined
    ? isCaptainCommissionCredited(setup)
    : setup.leadCommissionCreditedAt !== null || isCaptainCommissionCredited(setup);

/** Whether the captain's fixed commission part is credited: the server's stamp
 *  when it sent one, else "the setup is past admin approval". The whole captain
 *  commission being credited (at onboarded) implies the fixed part is too. */
export const isCaptainFixedCredited = (setup: NineSetup): boolean =>
  isCaptainCommissionCredited(setup) ||
  (setup.captainFixedCreditedAt === undefined
    ? isCurationPhaseStatus(setup.status)
    : setup.captainFixedCreditedAt !== null);

/** Whether the curator fixed commission part (7%) is credited: completed onward. */
export const isCuratorFixedCredited = (setup: NineSetup): boolean =>
  isCuratorCommissionCredited(setup) ||
  (setup.curatorFixedCreditedAt === undefined
    ? CURATOR_FIXED_CREDITED_STATUSES.includes(setup.status)
    : setup.curatorFixedCreditedAt !== null);

/** Whether the curator commission / variable part (3%) is credited; onboarded. */
export const isCuratorCommissionCredited = (setup: NineSetup): boolean =>
  setup.curatorCommissionCreditedAt === undefined
    ? CURATOR_VARIABLE_CREDITED_STATUSES.includes(setup.status)
    : setup.curatorCommissionCreditedAt !== null;

/** Non-editable once the record is either past the captain's control (a
 *  curator has claimed it — page-building is underway) or terminal on the
 *  captain side (`rejected` — the business said no, nothing left to fix).
 *  The curation half is derived from NINE_SETUP_CURATION_STATUSES (everything
 *  past `approved`) rather than re-listed, so adding a curation status here
 *  stays automatic; `rejected` is added explicitly since it's the one
 *  terminal captain-phase status. */
const NON_EDITABLE_NINE_SETUP_STATUSES: readonly NineSetupStatus[] = [
  ...NINE_SETUP_CURATION_STATUSES.filter((status) => status !== "approved"),
  "rejected",
];

/** True while a captain may still edit the row's details — up through
 *  `approved`, false from `claimed` onward and false for `rejected`. */
export function isEditableNineSetupStatus(status: NineSetupStatus): boolean {
  return !(NON_EDITABLE_NINE_SETUP_STATUSES as readonly string[]).includes(
    status
  );
}

/**
 * One row of `nine_setup`.
 *
 * Nullability mirrors the DDL exactly: only the NOT NULL columns
 * (businessName, customerMobile, captainId, status, createdAt, updatedAt) are
 * non-nullable here. Everything else is `| null` — the converter normalises
 * absent/empty values to null rather than "" or 0, so the UI can distinguish
 * "no fee recorded" from "₹0 fee" and render an em dash for the former.
 */
export interface NineSetup {
  /** `nine_setup.id`. Accepts `setupId` or `id` off the wire (see assumption 2). */
  setupId: string;

  // ── Business ──
  businessName: string;
  /** UNIQUE across the whole table — a duplicate create is a real, expected
   *  failure the UI must surface (see DUPLICATE_CUSTOMER_MOBILE in
   *  NineSetupApiService's FRIENDLY_MESSAGES). */
  customerMobile: string;
  location: string | null;
  latitude: number | null;
  longitude: number | null;

  // ── Captain (sales owner) ──
  captainId: string;
  captainMobile: string | null;

  // ── Intake ──
  googleFormLink: string | null;
  /** DB default TRUE. Curators skip the media step when this is false. */
  isMediaRequired: boolean;

  // ── State ──
  status: NineSetupStatus;
  /** Free-text context for the current status — the "why" behind `blocked`,
   *  `rejected` or `shelved`. */
  statusNote: string | null;

  // ── Optional relations ──
  /** The curator who claimed the row; null until `claimed`. */
  curatorId: string | null;
  /**
   * The curator's contact number. Mirrors `captainMobile`.
   *
   * FORWARD-COMPATIBLE: not on the deployed NineSetupDTO as of 2026-08-02 — the
   * row carries `curatorId` (a UUID) and nothing to call. The converter reads it
   * anyway and yields null while absent, so the captain card's curator contact
   * appears on its own once the backend adds the field.
   */
  curatorMobile: string | null;
  /** Null until `onboarded`; then points at the real row in `businesses`. */
  businessId: string | null;

  // ── Money ──
  /** Fee charged to the business. Units per assumption 1 above. */
  onboardingFee: number | null;
  /** Curator's cut. Units per assumption 1 above. */
  curatorFee: number | null;
  /** This setup's commission for the captain who owns it (whole rupees), in two
   *  parts (see captainFixedCommission): the fixed part joins the captain's
   *  totals at admin approval, the variable part at `onboarded`. Before that
   *  each is "pending" (shown, not settleable). Null when the server omits it. */
  captainCommission: number | null;
  /** The lead's cut of this setup (team rate minus the captain's rate). Same crediting rule as
   *  captainCommission. Null when absent. */
  leadCommission: number | null;
  /** The curator's commission for this setup (whole rupees). Null when the
   *  server has none yet (the earnings UI then falls back to `curatorFee`). */
  curatorCommission: number | null;
  /** When the captain + lead commissions moved into the totals (at
   *  `onboarded`); null until then. The server's own "earned" flag.
   *  FORWARD-COMPATIBLE: `undefined` means the server did not send the field at
   *  all (not "not credited yet") — the earnings UI then derives credit from
   *  `status` instead (see earningsRows.isCredited). */
  commissionCreditedAt?: string | null;
  /** When the curator commission moved into the curator's total (at
   *  `completed`); null until then. `undefined` when the field isn't on the
   *  wire, same as commissionCreditedAt. */
  curatorCommissionCreditedAt?: string | null;
  /** When the LEAD's cut was credited: at admin approval for leads on the backend's
   *  early-release list, otherwise same as commissionCreditedAt (at `onboarded`). Null until
   *  credited; `undefined` when the server doesn't send it (falls back to commissionCreditedAt). */
  leadCommissionCreditedAt?: string | null;
  /** The captain's commission split in two: the fixed part (credited when the
   *  admin approves the business) and the variable part (credited at
   *  `onboarded`). They add up to `captainCommission`. The fixed amount is exact
   *  once credited, an estimate before. Null/undefined when the server sends no
   *  split (the earnings UI then shows a single amount). */
  captainFixedCommission?: number | null;
  captainVariableCommission?: number | null;
  /** When the fixed part was credited; null until approval releases it.
   *  `undefined` = not on the wire (credit is then derived from status). */
  captainFixedCreditedAt?: string | null;
  /** The curator's commission split in two: fixed (7% of base, credited at completed)
   *  and variable (3% of base, credited at onboarded). */
  curatorFixedCommission?: number | null;
  curatorVariableCommission?: number | null;
  curatorFixedCreditedAt?: string | null;
  /** Whether the business has paid its onboarding fee, as the server sees it.
   *  FORWARD-COMPATIBLE: not on the wire yet. Until it is, the earnings UI
   *  reads payment from the captain's own submissions (captain rows) or infers
   *  it from status. Null when absent. */
  paymentStatus: PaymentStatus | null;
  /** Display names for labelling whose setup a row is; null when unknown. */
  captainName: string | null;
  curatorName: string | null;
  /** Target onboarding completion date if set. */
  targetDate?: string | null;
  enquiryNote?: string | null;
  enquiryTargetDate?: string | null;
  enquiredAt?: string | null;
  // ── Media collection details (set at media_collected) ──
  imageCount: number | null;
  pdfCount: number | null;
  pdfTotalPages: number | null;
  mediaCollectedBy: string | null;

  // ── Lifecycle timestamps (ISO-8601 strings; all server-stamped) ──
  contactedAt: string | null;
  acceptedAt: string | null;
  paidAt: string | null;
  claimedAt: string | null;
  mediaCollectedAt: string | null;
  submittedAt: string | null;
  onboardedAt: string | null;

  createdAt: string;
  updatedAt: string;
}

/**
 * The subset of columns a client is allowed to write. Derived from `NineSetup`
 * (rather than re-listed) so adding a column to the row type surfaces here too.
 *
 * Deliberately excluded:
 * - setupId / createdAt / updatedAt — server-owned identity and audit columns.
 * - every `*At` lifecycle stamp — the server derives these from the status
 *   transition. Sending client clocks would let skew reorder the funnel.
 * - captainId — derived from the bearer token (assumption 4).
 */
export type EditableNineSetupFields = Pick<
  NineSetup,
  | "businessName"
  | "customerMobile"
  | "location"
  | "latitude"
  | "longitude"
  | "captainMobile"
  | "googleFormLink"
  | "isMediaRequired"
  | "status"
  | "statusNote"
  | "curatorId"
  | "businessId"
  | "onboardingFee"
  | "curatorFee"
  | "imageCount"
  | "pdfCount"
  | "pdfTotalPages"
  | "targetDate"
>;

/**
 * Admin-only payload for update-nine-setup-enquiry.
 */
export interface UpdateNineSetupEnquiryPayload {
  setupId: string;
  enquiryNote?: string | null;
  enquiryTargetDate?: string | null;
}


/**
 * UI form state for creating a setup row.
 *
 * Only businessName and customerMobile are required — they are the two NOT NULL
 * columns a client supplies. `isMediaRequired` is optional because the DB
 * defaults it (TRUE); omitting it lets the server stay authoritative.
 *
 * Note this type is WIDER than the wire request: `status`, `statusNote`,
 * `curatorId` and `businessId` are inherited from EditableNineSetupFields but
 * NineSetupCreateRq has no home for them, so buildCreateNineSetupPayload drops
 * them (assumption 6 above). Setting one on a create form is a silent no-op —
 * send a follow-up updateNineSetup instead.
 */
export interface CreateNineSetupForm
  extends Partial<Omit<EditableNineSetupFields, "businessName" | "customerMobile">> {
  businessName: string;
  customerMobile: string;
}

/**
 * Body for `POST /api/v1/nine-setup/update-nine-setup`. Partial by design: the
 * payload builder drops `undefined` keys entirely, so a status-only update sends
 * `{ id, status, statusNote }` and nothing else.
 *
 * `null` and `undefined` are NOT interchangeable here — `null` means "clear this
 * column", `undefined` means "leave it alone".
 */
export interface UpdateNineSetupPayload extends Partial<EditableNineSetupFields> {
  /** Sent in the body as `id` — this endpoint has no path parameter. */
  setupId: string;
}

/**
 * Filters for the list call. The endpoint takes no query parameters, so these
 * are applied CLIENT-SIDE in NineSetupApiService.getNineSetups after conversion.
 * Omit for "everything I can see" — which is what the request fetches either way.
 */
export interface NineSetupListFilters {
  status?: NineSetupStatus;
  curatorId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Onboarding Calendar report — `GET /api/v1/nine-setup/onboardings-by-day`
// (operationId getSetupsByDay). Wire shapes below mirror the OpenAPI schemas
// NineSetupReportDTO → Month → Day → { CaptainCount, Setup } EXACTLY. Every
// field is optional because the spec marks none as required; the converter
// null-guards. Do not add speculative aliases here — if the backend renames a
// field, update these to match so the drift is visible in review.
// ─────────────────────────────────────────────────────────────────────────────

/** Query for the calendar report. `from`/`to` are inclusive local YYYY-MM-DD
 *  dates; omit `status` to let the server apply its default. */
export interface CalendarFilters {
  from: string;
  to: string;
  status?: CalendarStatus;
}

/** OpenAPI `CaptainCount`. */
export interface NineSetupCalendarCaptainDTO {
  captainId?: string;
  captainName?: string;
  count?: number;
}

/** OpenAPI `Setup`. `timestamp` is when the row entered the filtered status —
 *  the only event time the calendar uses. */
export interface NineSetupCalendarSetupDTO {
  nineSetupId?: string;
  captainId?: string;
  captainName?: string;
  businessId?: string;
  businessName?: string;
  status?: string;
  timestamp?: string;
  paidAt?: string;
  createdAt?: string;
  contactedAt?: string;
  acceptedAt?: string;
  submittedAt?: string;
  approvedAt?: string;
  claimedAt?: string;
  mediaCollectedAt?: string;
  completedAt?: string;
  onboardedAt?: string;
  /** Not sent by the backend yet — read as soon as they are (each stamped
   *  when the row reaches that status). */
  rejectedAt?: string;
  inProgressAt?: string;
  blockedAt?: string;
  inReviewAt?: string;
  welcomeKitPreparedAt?: string;
  handedOverAt?: string;
  curatorId?: string;
  curatorName?: string;
  curatorMobile?: string;
  captainMobile?: string;
  customerMobile?: string;
  targetDate?: string;
  enquiryNote?: string;
  enquiryTargetDate?: string;
}

/** OpenAPI `Day`. */
export interface NineSetupCalendarDayDTO {
  date?: string;
  total?: number;
  captains?: NineSetupCalendarCaptainDTO[];
  setups?: NineSetupCalendarSetupDTO[];
}

/** OpenAPI `Month`. `month` is "YYYY-MM". */
export interface NineSetupCalendarMonthDTO {
  month?: string;
  total?: number;
  days?: NineSetupCalendarDayDTO[];
}

/** OpenAPI `NineSetupReportDTO` — one element of the envelope's `contents`. */
export interface NineSetupCalendarReportDTO {
  from?: string;
  to?: string;
  status?: string;
  totalSetups?: number;
  months?: NineSetupCalendarMonthDTO[];
}

export interface CalendarCaptain {
  captainId: string;
  captainName: string;
  count: number;
}

/** One setup that hit the filtered status on a given day. */
export interface CalendarSetup {
  nineSetupId: string;
  captainId: string;
  captainName: string;
  businessId: string | null;
  businessName: string;
  /** Null when the server sent a status this client doesn't recognise. */
  status: CalendarStatus | null;
  /** ISO date-time the row entered the status (wire `timestamp`); null when absent. */
  eventAt: string | null;
  /** Lifecycle stamps — when the row REACHED each status, whatever its status
   *  is now. createdAt doubles as "targeted at". See STATUS_REACHED_AT. */
  createdAt: string | null;
  contactedAt: string | null;
  acceptedAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  claimedAt: string | null;
  mediaCollectedAt: string | null;
  completedAt: string | null;
  onboardedAt: string | null;
  paidAt: string | null;
  rejectedAt: string | null;
  inProgressAt: string | null;
  blockedAt: string | null;
  inReviewAt: string | null;
  welcomeKitPreparedAt: string | null;
  handedOverAt: string | null;
  /** The curator who claimed it; null until claimed. */
  curatorId: string | null;
  curatorName: string | null;
  curatorMobile: string | null;
  captainMobile: string | null;
  /** The business owner's number. */
  customerMobile: string | null;
  targetDate?: string | null;
  enquiryNote?: string | null;
  enquiryTargetDate?: string | null;
  enquiredAt?: string | null;
}

/** Whose work a calendar/targets view is about. */
export type CalendarRole = "captain" | "curator";

export interface CalendarDay {
  /** "YYYY-MM-DD" */
  date: string;
  total: number;
  captains: CalendarCaptain[];
  setups: CalendarSetup[];
}

export interface CalendarMonth {
  /** "YYYY-MM" */
  month: string;
  total: number;
  days: CalendarDay[];
}

export interface CalendarData {
  from: string;
  to: string;
  status: CalendarStatus | null;
  totalSetups: number;
  months: CalendarMonth[];
}

/** A date range of the calendar report (all statuses) for the Onboarding
 *  Insights views, when the calendar's month doesn't already cover it. */
export interface ReportRangeQuery {
  from: string;
  to: string;
}
